<template>
    <q-card flat v-if="rawData" >
        <!-- <q-btn label="showFine" @click="showFine"/> -->
        <q-card-section >
            <div class="text-body1">The area of interest (AOI) you entered is approximately 
                <span class="text-bold">{{ roundSigFig(rawData['areaKM2'],2) }}</span> KM<sup>2</sup> in the 
                <span class="text-bold">{{ ecoregionText }}</span>. 
                <div class="row text-caption q-mt-md" v-if="['planning', 'active'].includes(rawData.poly.features[0].properties.TYPE)">
                    <div>
                        NOTE: This area is part of a planned or active wind lease.
                        <q-btn dense size="xs" flat class="gray q-mb-xs" icon="help_outline" @click="infoDialogs['leaseWarning'].model = !infoDialogs['leaseWarning'].model" aria-label="Marine Life flags help"/>
                    </div>
                </div>

            </div>
            <div class="text-caption q-pt-sm" v-if="processedData[version]['inBounds'] == 'straddle'">NOTE: the AOI falls partially outside the study area.  Results are based on the portion of the AOI within the study area.</div>
        </q-card-section>
        <q-card-section id="flagSummary">
            <div class="row  q-ma-sm">
                <div class="col text-h6 text-uppercase">Summary of Key Considerations</div>
                <q-btn icon="print" @click="print('flagSummary', version, 'Flag')" v-if="!printing" aria-label="Print summary"/>
                <q-spinner v-else />
            </div>
            <q-separator />
            <div class="row text-body1 justify-center items-center print-hide">
                <q-select class="q-pa-sm" style="width: 300px" dense rounded outlined map-options v-model="framework" :options="frameworkOptions" label="Framework Used" @update:model-value="changeFramework"/>
            </div>
            <div class="text-caption q-mb-sm" v-if="framework.value =='marineLifeFlags'">
                The flags below were selected to draw your attention to significant marine features in the Area of Interest (AOI) that may need monitoring or mitigation. 
                <q-btn dense size="xs" flat class="gray q-mb-xs" icon="help_outline" @click="infoDialogs['mlFlagHelp'].model = !infoDialogs['mlFlagHelp'].model" aria-label="Marine Life flags help"/>
            </div>            
            <div class="text-caption q-mb-sm" v-if="framework.value =='ps6Flags'">
                The boxes below awere selected to highlight the components defining Critical Habitat used by the International Finance Corporation's <a class='underline' href='https://www.ifc.org/en/insights-reports/2012/ifc-performance-standard-6 ' target='_blank'>Performance Standard 6 Criteria</a> for Sustainable Natural Resource Management. 
                <q-btn dense size="xs" flat class="gray q-mb-none" icon="help_outline" @click="infoDialogs['ps6FlagHelp'].model = !infoDialogs['ps6FlagHelp'].model" aria-label="Marine Life flags help"/>
            </div>
            <div class="row text-center text-h5" v-if="framework.value =='marineLifeFlags'">
                <div class="col-2" v-for="configObj, theme in marineLifeFlags">
                    <div class="flag-box q-pa-sm" @click="setFlagDet(theme)" :class="boxClass(theme)">
                        <div :class="presentClass(theme)">
                            <div><q-icon v-svg="getIcon(flagIcons[theme])" :color="iconColor(theme)"/></div>
                            <div>{{ flagTitleAbbrs[theme] }}</div>
                        </div>
                    </div>
                </div> 
            </div>
            <div class="row text-center text-h5" v-if="framework.value =='ps6Flags'">
                <div class="col-2" v-for="configObj, theme in ps6Flags">
                    <div class="flag-box q-pa-sm" @click="setFlagDet(theme)" :class="boxClass(theme)">
                        <div :class="presentClass(theme)">
                            <div><q-icon v-svg="getIcon(flagIcons[theme])" :color="iconColor(theme)"/></div>
                            {{ flagTitleAbbrs[theme] }}
                        </div>
                    </div>
                </div>             
            </div>
            <q-separator v-if="framework.value == 'marineLifeFlags' && flagCategory=='habitats'" color="black" class="connection-line box-1"/>
            <q-separator v-if="framework.value == 'marineLifeFlags' && flagCategory=='features'" color="black" class="connection-line box-2"/>
            <q-separator v-if="framework.value == 'marineLifeFlags' && flagCategory=='species'" color="black" class="connection-line box-3"/>
            <q-separator v-if="framework.value == 'marineLifeFlags' && flagCategory=='diversity1'" color="black" class="connection-line box-4"/>
            <q-separator v-if="framework.value == 'marineLifeFlags' && flagCategory=='communities'" color="black" class="connection-line box-5"/>

            <q-separator v-if="framework.value == 'ps6Flags' && flagCategory=='endangered'" color="black" class="connection-line box-1"/>
            <q-separator v-if="framework.value == 'ps6Flags' && flagCategory=='endemic'" color="black" class="connection-line box-2"/>
            <q-separator v-if="framework.value == 'ps6Flags' && flagCategory=='diversity2'" color="black" class="connection-line box-3"/>
            <q-separator v-if="framework.value == 'ps6Flags' && flagCategory=='threatened'" color="black" class="connection-line box-4"/>
            <q-separator v-if="framework.value == 'ps6Flags' && flagCategory=='processes'" color="black" class="connection-line box-5"/>
            <q-separator v-if="framework.value == 'ps6Flags' && flagCategory==''" color="black" class="connection-line box-6"/>

            <div v-for="configObj, theme in marineLifeFlags" >
                <div class="row q-ma-xs"  v-if="flagCategory == theme">
                    <div class="col-12 flag-detBox text-left q-pt-sm" v-if="flagData && flagData['themePresent'][theme]">
                        <div class="text-center text-body1 text-bold text-uppercase">{{ flagPNames[theme] }}</div>
                        <div class="q-pa-sm">{{ flagDescText[theme] }}</div>
                        <ul class="" v-for="value, attr in flagData.results[theme]">
                            <!-- this is for the non ESA birds -->
                            <li class="" v-if="value >0 && attr.toString() != 'FKS_BIC' && attr.toString() != 'FKS_BIO'" >
                                <div class="row">
                                    <div class="col-9"> {{ marineLifeFlags[theme][attr][value].text }} </div>
                                    <div class="col-3 myLink pointer q-pl-sm" v-if="marineLifeFlags[theme][attr][value].connect" @click="goTo(marineLifeFlags[theme][attr][value].connect)">More Info ></div>
                                </div>
                            </li>
                        </ul>

                        <!-- Added this later for the ESA birds, which get nested: coastal and offshore under single header -->
                        <ul v-if="flagData.results[theme]['FKS_BIC'] >0 || flagData.results[theme]['FKS_BIO'] >0  ">
                            <li>One or more ESA listed species of birds may use this area at high levels. The area selected includes both coastal and offshore data for these species. </li>
                            <div v-for="value, attr in flagData.results[theme]">
                                <ul v-if="value >0 && ((attr.toString()).includes('BIC') || (attr.toString()).includes('BIO'))  &&  (!(attr.toString()).includes('BICS') || !(attr.toString()).includes('BIOS'))">
                                    <li >                                     
                                        <div class="row">
                                            <div class="col-9"> {{ marineLifeFlags[theme][attr][value].text }} </div>
                                            <div class="col-3 myLink pointer q-pl-sm" v-if="marineLifeFlags[theme][attr][value].connect" @click="goTo(marineLifeFlags[theme][attr][value].connect)">More Info ></div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </ul>
                        
                        <div class="text-caption text-left q-pa-sm text-italic">{{ flagNoteText[theme] }}</div>
                    </div>
                    <div class="col-12 flag-detBox text-center q-pt-sm" v-else>
                        <div class="text-center text-body1 text-bold text-uppercase">{{ flagPNames[theme] }}</div>
                        <div class="text-left q-pa-sm">{{ flagDescText[theme] }}</div>
                        <ul><li class="text-left q-pa-sm">{{ flagNoDataText[theme] }}</li></ul>
                        <div class="text-caption text-left q-pa-sm text-italic">{{ flagNoteText[theme] }}</div>
                    </div>
                </div>
            </div>
            <div v-for="configObj, theme in ps6Flags" >
                <div class="row q-ma-xs"  v-if="flagCategory == theme">
                    <div class="col-12 flag-detBox text-left q-pt-sm" v-if="flagData && flagData['themePresent'][theme]">
                        <div class="text-center text-body1 text-bold text-uppercase">{{ flagPNames[theme] }}</div>
                        <div class="q-pa-sm">{{ flagDescText[theme] }}</div>

                        <!-- this is for the non ESA birds, which get nested -->
                        <ul class="" v-for="value, attr in flagData.results[theme]">
                            <li class="" v-if="value > 0  && attr.toString() != 'PES_BIC' && attr.toString() != 'PES_BIO'" >
                                <div class="row">
                                    <div class="col-9"> {{ ps6Flags[theme][attr][value].text }} </div>
                                    <div class="col-3 myLink pointer q-pl-sm" v-if="ps6Flags[theme][attr][value].connect" @click="goTo(ps6Flags[theme][attr][value].connect)">More Info ></div>
                                </div>
                            </li>
                        </ul>


                        <!-- this is for the ESA birds, which get nested: coastal and offshore under single header -->
                        <ul v-if="flagData.results[theme]['PES_BIC'] >0 || flagData.results[theme]['PES_BIO'] >0  ">
                            <li>One or more ESA listed species of birds may use this area at high levels. The area selected includes both coastal and offshore data for these species. </li>
                            <div v-for="value, attr in flagData.results[theme]">
                                <ul v-if="value >0 && (attr.toString() == 'PES_BIC' || attr.toString() == 'PES_BIO')">
                                    <li >                                     
                                        <div class="row">
                                            <div class="col-9"> {{ ps6Flags[theme][attr][value].text }} </div>
                                            <div class="col-3 myLink pointer q-pl-sm" v-if="ps6Flags[theme][attr][value].connect" @click="goTo(ps6Flags[theme][attr][value].connect)">More Info ></div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </ul>

                        <div class="text-caption text-left q-pa-sm text-italic">{{ flagNoteText[theme] }}</div>

                        <!-- This was to only show the warning if the AOI was a predefined lease, but decided to always include 
                        <div class="text-caption text-left q-pa-sm q-pt-none text-italic" v-if="['planning', 'active'].includes(rawData.poly.features[0].properties.TYPE) && theme == 'endangered'">
                            Note that the tool does not reflect any mitigation or other conservation requirements already in place for this area.
                        </div> -->

                    </div>
                    <div class="col-12 flag-detBox text-center q-pt-sm" v-else>
                        <div class="text-center text-body1 text-bold text-uppercase">{{ flagPNames[theme] }}</div>
                        <div class="text-left q-pa-sm">{{ flagDescText[theme] }}</div>
                        <ul><li class="text-left q-pa-sm">{{ flagNoDataText[theme] }}</li></ul>
                        <div class="text-caption text-left q-pl-sm text-italic">{{ flagNoteText[theme] }}</div>
                    </div>
                </div>
            </div>
        </q-card-section>

    </q-card>
    <div class="row text-caption q-pl-lg q-pr-md">
        <div>
            NOTE: The tool currently does not reflect any mitigation or conservation requirements already in place for this area.
            <q-btn dense size="xs" flat class="gray q-mb-xs" icon="help_outline" @click="infoDialogs['leaseWarning'].model = !infoDialogs['leaseWarning'].model" aria-label="Marine Life flags help"/>
        </div>
    </div>
    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model">
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>
</template>
<script setup>
import { toRefs, ref, computed, onMounted } from "vue";
import { useMapItems } from "../composables/useMapItems"
import { useHelpers } from "../composables/useHelpers"
import { useRouter, useRoute } from 'vue-router'
import { marineLifeFlags, ps6Flags } from '../assets/FlagConfig.json'
import { useSvgIcon } from '../composables/useSvgIcon';
import InfoDialog from "./InfoDialog.vue";

const router = useRouter()
const route = useRoute()
const { print, printing, processedData, setProcessedData, ecos, activeLayerID, loadDataByID, activeFlagType, setActiveFlagType, activeFlagValue, setActiveSpGroups, setActiveFlagValue, setActiveCoastalHabitat } = useMapItems()
const { roundSigFig, arrayFindObjectByProp, logGAevent } = useHelpers()
const { getIcon } = useSvgIcon();

const props = defineProps({
    rawData: { type: Object },
    version: {type: Number}
})

const { rawData, version } = toRefs(props)

const ecoregionText = computed(() => {
    let text = ""
    let wordEcoregion = "ecoregion"
    
    // console.log(data.value['ecoregions'])
    if (rawData.value['ecoregions'].length >1){wordEcoregion += "s"}
    // console.log(data)
    if (rawData.value['ecoregions']){
        // console.log(data.value['ecoregions'])
        rawData.value['ecoregions'].forEach((er) =>{
            text += ecos[er] + " & "
        })
        text = text.slice(0, -3) + " " + wordEcoregion
    }
    return text
})

const framework = computed({
    get: () => {
        const fr = activeFlagType.value
        const frObj = arrayFindObjectByProp(frameworkOptions.value, "value", fr)
        return frObj
    },
    set: (value) => {
 
        setActiveFlagType(value.value)
        logGAevent("flag_framework", value.label, "")
    }
});

const frameworkOptions = ref([{label: "Marine Life & Habitat Flags", value: "marineLifeFlags"},{label: "IFC Performance Standard 6", value: "ps6Flags"}])

const flagCategory = computed({
    get: () => {
        return activeFlagValue.value
    },
    set: (value) => {
        setActiveFlagValue(value)
    }
});

const setFlagDet = (flagCat) =>{
    flagCategory.value = flagCat;
    setActiveFlagValue(flagCat)
}


const flagData =  computed(() =>{
    // console.log(rawData.value )
    const values = {}
    const results = {}
    const themes ={}
    Object.keys(activeFlags.value).forEach((theme) =>{
        values[theme] = {}
        results[theme] = {}
        themes[theme] = false
        Object.keys(activeFlags.value[theme]).forEach((attr) =>{
            values[theme][attr] = []
        })
    })

    if (
        !processedData.value[version.value] || 
        processedData.value[version.value]['FLAGS'] === undefined ||
        processedData.value[version.value]['FLAGS'][activeFlagType.value] === undefined ||
        processedData.value[version.value]['FLAGS'][activeFlagType.value]['a'] === undefined
    ){
        if (rawData.value && rawData.value["FLAGS"] && rawData.value["FLAGS"].length >0){
            rawData.value["FLAGS"].forEach((cell) => {
                Object.keys(values).forEach((theme) =>{
                    Object.keys(values[theme]).forEach((attr) =>{
                        values[theme][attr].push(cell[attr])
                        if (cell[attr] > 0){ // if there's any non-zero value, mark that theme as having something
                            themes[theme] = true;
                        }
                    })
                })
            })
        
            Object.keys(values).forEach((theme) =>{
                Object.keys(values[theme]).forEach((attr) =>{
                    results[theme][attr] = Math.max(...values[theme][attr])
                })
            })
            
            // console.log(results)
            const final = {results: results, themePresent:themes}
            setProcessedData(version.value, "FLAGS", activeFlagType.value, 'a', final)
            return final
        }
        else{ return null}
    }
    else{
        // console.log("Loading ", processedData.value[version.value]['FLAGS'][activeFlagType.value]['a'])
        return processedData.value[version.value]['FLAGS'][activeFlagType.value]['a']
    }
})


const boxClass = (name) =>{
  let c = ""
  if (flagCategory.value == name){
    c += ' flag-box-highlight '
  } 
  return c
}

const presentClass = (name) =>{
  // this applies the logic for the green highlight in the box if the feature is present
  if (flagData.value && flagData.value['themePresent'][name]){
    return 'flag-box-present pointer'
  }
  else{
    return "pointer"
  }
}

const iconColor = (name) =>{
    if (flagData.value && flagData.value['themePresent'][name]){
    return 'white'
  }
  else{
    return "black"
  }
}

const coastalOffshore = computed(() =>{
    let coastal = true
    let offshore = true
    if (rawData.value['FLAGS']){
        rawData.value['FLAGS'].forEach((cell) =>{
            if (cell['COAST'] == 1){coastal = true}
            if (cell['COAST'] == 0){offshore = true}
        })
    }
    
    return {
        coastal: coastal,
        offshore: offshore
    }
})

const changeFramework = () =>{
    if (framework.value.value == 'marineLifeFlags'){
        setFlagDet('habitats')
    }
    else{
        setFlagDet('endangered')
    }
}

const activeFlags =  computed(() =>{
    if (framework.value.value == 'marineLifeFlags'){
        return marineLifeFlags
    }
    else if (framework.value.value == 'ps6Flags'){
        return ps6Flags
    }
    else{
        return null
    }
})

const goTo = (obj) =>{
    if(obj.params){
        if (obj.params.type == 'activeSpGroup'){
            setActiveSpGroups(obj.params.theme, obj.params.value)
        }
        if (obj.params.type == 'activeCoastalHab'){
            setActiveCoastalHabitat(obj.params.value)
        }
    }
    if (obj.link){
        // console.log(route.fullPath)
        const path = `${obj.link}`
        router.push(path)
    }
    // console.log(obj)
}

const flagPNames = {
    "habitats" : "Habitats",
    "features" :"Features",
    "species" : "Species",
    "diversity1" : "High Diversity",
    "diversity2" : "High Diversity",
    "communities" : "Communities at Sea",
    "endangered" : "Endangered species",
    "endemic" : "High Conservation Need",
    "threatened" : "Threatened and unique ecosystems",
    "processes" : "Key evolutionary processes"
}
const flagIcons = {
    "habitats" : "coastalV2",
    "features" :"featuresV2",
    "species" : "speciesV2",
    "diversity1" : "speciesV2",
    "diversity2" : "speciesV2",
    "communities" : "peopleV2",
    "endangered" : "speciesV2",
    "endemic" : "speciesV2",
    "threatened" : "speciesV2",
    "processes" : "featuresV2"
}
const flagTitleAbbrs ={
    "habitats" : "HAB",
    "features" :"FEA",
    "species" : "SPE",
    "diversity1" : "DIV",
    "diversity2" : "DIV",
    "communities" : "COM",
    "endangered" : "ESA",
    "endemic" : "HCN",
    "threatened" : "THR",
    "processes" : "PRO"
}

const flagDescText ={
    "habitats" : "This section highlights important habitats within the area of interest including coastal habitats that are critical for supporting many species life cycles (e.g. wetlands, seagrasses, ... ) and deep-sea corals. ",
    "features" :"This section highlights important features that support key species across the region. ",
    "species" : "This section highlights species that are considered important across the region, either because they are actively managed by the Fisheries Councils, or considered endangered by the Endangered Species Act (ESA).",
    "diversity1" : "This section highlights areas that are considered high diversity.",
    "diversity2" : "This section highlights areas that are considered high diversity.",
    "communities" : "This section highlights areas that are used by one or more fishing communities in the area.",
    "endangered" : "This section highlights species that are considered Endangered or Threatened. It includes species that are federally considered endangered or threatened, and part of the Endangered Species Act (ESA). Additionally, it includes species considered Endangered by one of the states within our study area.",
    "endemic" : "This section highlights species that are considered of high conservation priority by the Northeast Fish and Wildlife Diversity Technical Committee (NE RSGCN). These are often species that are considered endemic or have limited or declining ranges.",
    "threatened" : "This section highlights threatened and unique habitats within the area of interest. These include critical coastal habitats that are used by many species as part of their migratory pathways (e.g. wetlands, seagrasses, ... ) and critical offshore habitats such as deep-sea corals and hard-bottom areas.",
    "processes" : "This section highlights areas that have a particular importance for evolutionary processes or present important ecological attributes. This includes important areas for diadromous species and breeding areas."
}

const flagNoteText ={
    "habitats" : "Be aware that this section may be missing important habitats due to lack of data. ",
    "features" :"Note that currently this only includes hardbottom features. Other important seafloor and oceanographic features may be included in future versions. ",
    "species" : "Note that, because of data limitations, not all ESA listed species are included.  Also, the tool does not reflect any mitigation or other conservation requirements already in place for this area.",
    "diversity1" : "Note that not all the species in the area were assessed as part of this analysis, as many speciess are data limited. This may understimate biodiversity levels in some areas.",
    "diversity2" : "Note that not all the species in the area were assessed as part of this analysis, as many speciess are data limited. This may understimate biodiversity levels in some areas.",
    "communities" : "Note that this dataset only includes a subset of communities due to data confidentiality, so this may underestimate the communities' dependence on the area selected.",
    "endangered" : "Note that not all ESA listed species are included due to data limitations. Also, the tool does not reflect any mitigation or other conservation requirements already in place for this area. ",
    "endemic" : "Due to the limited data available, we were not able to include all species, such as some of the most rare species.",
    "threatened" : "Note than other important habitats, such as oyster reefs and kelps, were not inlcuded due to data limitations.",
    "processes" : ""

}

const flagNoDataText = {
    "habitats" : "The area of interest either has none of the habitats included in this section, or we currently don't have enough data to be able to determine the presence of this type of habitat. Please review data considerations for more information.",
    "features" :"The area of interest either has none of the features included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information. ",
    "species" : "The area of interest either has none of the species included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information.",
    "diversity1" : "The area of interest either has levels of diversity that are lower than the ecoregional threshdolds, or we currently don't have enough data to be able to determine accurate numbers. Please, review data considerations for more information. ",
    "diversity2" : "The area of interest either has none of the species included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information. ",
    "communities" : "The area of interest either has none of the communities reviewed, or we currently don't have enough data to be able to determine accurate numbers. Please, review data considerations for more information. ",
    "endangered" : "The area of interest either has none of the species included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information. ",
    "endemic" : "The area of interest either has none of the species included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information.",
    "threatened" : "The area of interest either has none of the habitats included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information.",
    "processes" : "The area of interest either has none of the attributes included in this section, or we currently don't have enough data to be able to determine their presence. Please, review data considerations for more information." 
}


const infoDialogs = ref({
    mlFlagHelp: {
        pName: "Marine Life Flags",
        model: false,
        text: "Marine Life flags are intended to draw your attention to significant marine features in the Area of Interest (AOI) that may need monitoring or mitigation.  These flags are NOT all regulatory requirements, but may highlight potential opportunities for action. Click on each of the boxes to get a detailed description of each flag and link directly to that section of the tool. You can also follow the menu to the left to dig deeper into these data."
    },
    ps6FlagHelp:{
        pName: "PS6 Flags",
        model: false,
        text: "The PS6 flags are intended to highlight the components defining Critical Habitat used by the International Finance Corporation's <a class='underline' href='https://www.ifc.org/en/insights-reports/2012/ifc-performance-standard-6 ' target='_blank'>Performance Standard 6 Criteria</a> for Sustainable Natural Resource Management. This criteria recognizes that protecting and conserving biodiversity and its ability to change and envolve is fundamental to sustainable dvelopment. It defines critical habitat as a subset of habitats that have high biodiversity values as defined by its species, features, processes, and services provided. These criteria are NOT all regulatory requirements, but may highlight potential voluntary opportunities for action. This is the first known effort to match these international criteria to regional datasets. More work is needed to further refine these criteria as new data are acquired. "
  
    },
    leaseWarning:{
        pName: "Other Requirements",
        model: false,
        text: "Data in this tool does not reflect the mitigation requirements included for endangered and threatened species as part of a project's approvals by the Bureau of Offshore Energy Management (BOEM). For example, there are seasonal restrictions for certain offshore activities that could correspond with high estimated density for protected species. For more information about the specific conditions, go to <a href='https://www.boem.gov/renewable-energy/offshore-renewable-activities' target='_blank'>Offshore Renewable Activities | Bureau of Ocean Energy Management</a>."
    }
})

onMounted(() => {

    if (activeLayerID.value != "FLAGS"){
        loadDataByID('FLAGS', version)
    }

    // don't overwrite saved data if coming from base64 link
    if (processedData.value[version.value] && processedData.value[version.value]['polyID'] == undefined){
        // setProcessedData(version.value, "FLAGS", null, null, data.value.FLAGS)
        setProcessedData(version.value, "areaKM2", null, null, rawData.value.areaKM2)
        setProcessedData(version.value, "ecoregions", null, null, rawData.value.ecoregions)
        setProcessedData(version.value, "majEcoregion", null, null, rawData.value.majEcoregion)
        setProcessedData(version.value, "name", null, null, rawData.value.name)
        setProcessedData(version.value, "poly", null, null, rawData.value.poly)
        setProcessedData(version.value, "polyID", null, null, rawData.value.polyID)
        setProcessedData(version.value, "numCells", null, null, rawData.value.numCells)
        setProcessedData(version.value, "type", null, null, 'processed')
        setProcessedData(version.value, "coastalOffshore", null, null, coastalOffshore.value)
    }
})


const showFine = () =>{
    // loadDataByID('SubGridExpanded', version)
    loadDataByID('NJ', version)
}
</script>
<style scoped>
.flag-box{
    height: 80px;
    outline: 1px solid black;
    outline-offset: -4px;
    padding: 4px;
    min-height: 60px;
}
.flag-detBox{
    border: 3px solid black;
    min-height: 300px;
}

.flag-box-highlight{
    outline: 3px solid black;
    outline-offset: -4px;
}
.connection-line{
    rotate: 90deg;
    max-width: 11px;
    height: 3px;
    position: relative;
}
.flag-box-present{
    background-color: #1D8177;
    color: white;
    height: 100%;
    width: 100%;
}
.box-1{
    left: 7%;
}
.box-2{
    left: 24%;
}
.box-3{
    left: 40%;
}
.box-4{
    left: 57%;
}
.box-5{
    left: 74%;
}
.box-6{
    left: 90%;
}


</style>